import { GridColumns, GridCellParams } from '@mui/x-data-grid-pro';
import { XGridDefaultColumn } from 'app/components/Common/XGrid/cells/XGridDefaults';
import { XGridSideBarViewCell } from 'app/components/Common/XGrid/cells/XGridSideBarViewCell';
import { XGridLinkCell } from 'app/components/Common/XGrid/cells/XGridLinkCell';
import { XGridOverflowCell } from 'app/components/Common/XGrid/cells/XGridOverflowCell';
import { convertToFormat } from 'app/helpers/dateHelper';
import { formatToAmericanNotaion } from 'app/helpers/numberHelper';
import { missingField } from 'app/constants';

interface IMarginOrderColumns {
  orderId: string;
  payPackageStatus: string;
  jdhPostingStatus: string;
  suppressInJdh: boolean;
  positions: number;
  division: string;
  facility: string; // Facility name for display
  facilityId: string;
  discipline: string;
  specialty: string;
  subspecialty: string;
  shift: string;
  minimumRequiredHours: number;
  hoursPerWeek: number;
  hoursPerShift: number;
  targetMarginDefault: number;
  targetMarginDay: number;
  targetMarginNight: number;
  targetMarginDayNight: number;
  payRangeMinMaxPercentage: number;
  defaultWeeklyPay: number;
  actualMargin: number;
  weeklyGrossPay: number;
  minimumWeeklyGrossPay: number;
  maximumWeeklyGrossPay: number;
  billRate: number;
  vmsFee: number;
  otherFee: number;
  payRate: number;
  lodgingPerDiem: number;
  mealPerDiem: number;
  created: string;
  createdBy: string;
  lastUpdated: string;
  lastUpdatedBy: string;
  orderCreated: string;
  orderVerified: string;
  unit: string;
  affiliate: string;
  facilityStatus: string;
  orderCreatedBy: string;
  orderLastUpdatedBy: string;
}

const MarginOrderDefaultCell = {
  ...XGridDefaultColumn,
  flex: 0,
  sortable: false,
};

// Helper function to format percentage values
const formatPercentage = (value: number | null | undefined): string => {
  if (value === null || value === undefined || isNaN(value)) return '--';
  return `${value.toFixed(2)}%`;
};

// Helper function to format currency values
const formatCurrency = (value: number | null | undefined): string => {
  if (value === null || value === undefined || isNaN(value)) return '--';
  return `$${value.toFixed(2)}`;
};

// Helper function to format currency values as whole numbers
const formatCurrencyWhole = (value: number | null | undefined): string => {
  if (value === null || value === undefined || isNaN(value)) return '--';
  return `$${Math.round(value)}`;
};

// Helper function to format date/time values
const formatDateTime = (value: string | null | undefined): string => {
  if (!value) return '--';
  return convertToFormat(value, 'MM/DD/YYYY hh:mm:ss A');
};

// Helper function to format date values
const formatDate = (value: string | null | undefined): string => {
  if (!value) return '--';
  return convertToFormat(value, 'MM/DD/YYYY hh:mm:ss A');
};

// Helper function to format Yes/No values
const formatYesNo = (value: boolean | null | undefined): string => {
  if (value === null || value === undefined) return '--';
  return value ? 'Yes' : 'No';
};

// Helper function to format number values
const formatNumber = (value: number | null | undefined): string => {
  if (value === null || value === undefined || isNaN(value)) return '--';
  return formatToAmericanNotaion(value);
};

// Custom cell renderer for Facility ID that opens facility side panel
const FacilityIdSideBarViewCell = (path: string, fieldList: string[] = [], isHoverView?: boolean) => {
  return (params: GridCellParams) => {
    // Create a modified params object with facility-specific preview viewer fields
    const modifiedParams = {
      ...params,
      row: {
        ...params.row,
        previewDrawerViewerId: params.row.facilityId,
        previewDrawerViewerType: 2, // Type 2 for facility side panel
      },
    };
    // Use XGridSideBarViewCell with the modified params
    const SideBarCell = XGridSideBarViewCell(path, fieldList, isHoverView);
    return SideBarCell(modifiedParams);
  };
};


export const marginOrdersViewInfo = (t): GridColumns => [
  // Checkbox column (handled by XGrid automatically)
  
  // Order ID - Default column, clickable
  {
    ...MarginOrderDefaultCell,
    field: 'orderId',
    headerName: 'Order ID',
    width: 120,
    sortable: true,
    renderCell: XGridOverflowCell(XGridSideBarViewCell('/order/{0}', ['orderId'], true))(
      XGridSideBarViewCell('/order/{0}', ['orderId']),
    ),
  },
  
  // Pay Package Status - Default column
  {
    ...MarginOrderDefaultCell,
    field: 'payPackageStatus',
    headerName: 'Pay Package Status',
    width: 150,
    valueGetter: (params) => {
      const status = params.row.payPackageStatus;
      if (!status) return '--';
      return status;
    },
  },
  
  // JDH Posting Status - Default column
  {
    ...MarginOrderDefaultCell,
    field: 'jdhPostingStatus',
    headerName: 'JDH Posting Status',
    width: 150,
    valueGetter: (params) => {
      const status = params.row.jdhPostingStatus;
      if (!status) return '--';
      return status.charAt(0).toUpperCase() + status.slice(1);
    },
  },
  
  // Suppress in JDH - Not a default column
  {
    ...MarginOrderDefaultCell,
    field: 'suppressInJdh',
    headerName: 'Suppress in JDH',
    width: 130,
    hide: true,
    valueGetter: (params) => formatYesNo(params.row.suppressInJdh),
  },
  
  // Positions - Not a default column
  {
    ...MarginOrderDefaultCell,
    field: 'positions',
    headerName: 'Positions',
    width: 100,
    hide: true,
    valueGetter: (params) => formatNumber(params.row.positions),
  },
  
  // Division - Default column
  {
    ...MarginOrderDefaultCell,
    field: 'division',
    headerName: 'Division',
    width: 120,
    valueGetter: (params) => {
      const value = params.row.division;
      return value === missingField ? '' : value;
    },
  },
  
  // Facility ID - Default column, clickable (opens side panel)
  {
    ...MarginOrderDefaultCell,
    field: 'facilityId',
    headerName: 'Facility ID',
    width: 120,
    renderCell: XGridOverflowCell(FacilityIdSideBarViewCell('/facility/{0}', ['facilityId'], true))(
      FacilityIdSideBarViewCell('/facility/{0}', ['facilityId']),
    ),
  },
  
  // Facility Name - Default column, clickable
  {
    ...MarginOrderDefaultCell,
    field: 'facility',
    headerName: 'Facility Name',
    width: 200,
    sortable: true,
    valueGetter: (params) => {
      const value = params.row.facility;
      return value === missingField ? '' : value;
    },
    renderCell: XGridOverflowCell(XGridLinkCell('/facility/{0}', ['facilityId'], true))(
      XGridLinkCell('/facility/{0}', ['facilityId']),
    ),
  },
  
  // Discipline - Default column
  {
    ...MarginOrderDefaultCell,
    field: 'discipline',
    headerName: 'Discipline',
    width: 120,
    sortable: true,
  },
  
  // Specialty - Default column
  {
    ...MarginOrderDefaultCell,
    field: 'specialty',
    headerName: 'Specialty',
    width: 120,
    sortable: true,
  },
  
  // Subspecialty - Default column
  {
    ...MarginOrderDefaultCell,
    field: 'subspecialty',
    headerName: 'Subspecialty',
    width: 130,
    sortable: true,
  },
  
  // Shift - Default column
  {
    ...MarginOrderDefaultCell,
    field: 'shift',
    headerName: 'Shift',
    width: 120,
  },
  
  // Minimum Required Hours - Default column
  {
    ...MarginOrderDefaultCell,
    field: 'minimumRequiredHours',
    headerName: 'Minimum Required Hours',
    width: 180,
    valueGetter: (params) => formatNumber(params.row.minimumRequiredHours),
  },
  
  // Hours Per Week - Not a default column
  {
    ...MarginOrderDefaultCell,
    field: 'hoursPerWeek',
    headerName: 'Hours Per Week',
    width: 130,
    hide: true,
    valueGetter: (params) => formatNumber(params.row.hoursPerWeek),
  },
  
  // Hours Per Shift - Not a default column
  {
    ...MarginOrderDefaultCell,
    field: 'hoursPerShift',
    headerName: 'Hours Per Shift',
    width: 130,
    hide: true,
    valueGetter: (params) => formatNumber(params.row.hoursPerShift),
  },
  
  // Target Margin - Default - Default column
  {
    ...MarginOrderDefaultCell,
    field: 'targetMarginDefault',
    headerName: 'Target Margin - Default',
    width: 180,
    valueGetter: (params) => formatPercentage(params.row.targetMarginDefault),
  },
  
  // Target Margin - Day - Not a default column
  {
    ...MarginOrderDefaultCell,
    field: 'targetMarginDay',
    headerName: 'Target Margin - Day',
    width: 160,
    hide: true,
    valueGetter: (params) => formatPercentage(params.row.targetMarginDay),
  },
  
  // Target Margin - Night - Not a default column
  {
    ...MarginOrderDefaultCell,
    field: 'targetMarginNight',
    headerName: 'Target Margin - Night',
    width: 170,
    hide: true,
    valueGetter: (params) => formatPercentage(params.row.targetMarginNight),
  },
  
  // Target Margin - Day/Night - Not a default column
  {
    ...MarginOrderDefaultCell,
    field: 'targetMarginDayNight',
    headerName: 'Target Margin - Day/Night',
    width: 190,
    hide: true,
    valueGetter: (params) => formatPercentage(params.row.targetMarginDayNight),
  },
  
  // Pay Range Min/Max Percentage - Not a default column
  {
    ...MarginOrderDefaultCell,
    field: 'payRangeMinMaxPercentage',
    headerName: 'Pay Range Min/Max Percentage',
    width: 220,
    hide: true,
    valueGetter: (params) => formatPercentage(params.row.payRangeMinMaxPercentage),
  },
  
  // Default Weekly Pay - Default column
  {
    ...MarginOrderDefaultCell,
    field: 'defaultWeeklyPay',
    headerName: 'Default Weekly Pay',
    width: 160,
    valueGetter: (params) => formatCurrencyWhole(params.row.defaultWeeklyPay),
  },
  
  // Actual Margin - Default column
  {
    ...MarginOrderDefaultCell,
    field: 'actualMargin',
    headerName: 'Margin',
    width: 130,
    valueGetter: (params) => formatPercentage(params.row.actualMargin),
  },
  
  // Weekly Gross Pay - Default column
  {
    ...MarginOrderDefaultCell,
    field: 'weeklyGrossPay',
    headerName: 'Weekly Gross Pay',
    width: 160,
    valueGetter: (params) => formatCurrencyWhole(params.row.weeklyGrossPay),
  },
  
  // Minimum Weekly Gross Pay - Default column
  {
    ...MarginOrderDefaultCell,
    field: 'minimumWeeklyGrossPay',
    headerName: 'Minimum Weekly Gross Pay',
    width: 200,
    valueGetter: (params) => formatCurrencyWhole(params.row.minimumWeeklyGrossPay),
  },
  
  // Maximum Weekly Gross Pay - Default column
  {
    ...MarginOrderDefaultCell,
    field: 'maximumWeeklyGrossPay',
    headerName: 'Maximum Weekly Gross Pay',
    width: 200,
    valueGetter: (params) => formatCurrencyWhole(params.row.maximumWeeklyGrossPay),
  },
  
  // Bill Rate - Default column
  {
    ...MarginOrderDefaultCell,
    field: 'billRate',
    headerName: 'Bill Rate',
    width: 100,
    valueGetter: (params) => formatCurrency(params.row.billRate),
  },
  
  // VMS Fee - Default column
  {
    ...MarginOrderDefaultCell,
    field: 'vmsFee',
    headerName: 'VMS Fee',
    width: 100,
    valueGetter: (params) => formatCurrency(params.row.vmsFee),
  },
  
  // Other Fee - Default column
  {
    ...MarginOrderDefaultCell,
    field: 'otherFee',
    headerName: 'Other Fee',
    width: 100,
    valueGetter: (params) => formatCurrency(params.row.otherFee),
  },
  
  // Pay Rate - Default column
  {
    ...MarginOrderDefaultCell,
    field: 'payRate',
    headerName: 'Pay Rate',
    width: 100,
    valueGetter: (params) => formatCurrency(params.row.payRate),
  },
  
  // Lodging Per Diem - Default column
  {
    ...MarginOrderDefaultCell,
    field: 'lodgingPerDiem',
    headerName: 'Lodging Per Diem',
    width: 150,
    valueGetter: (params) => formatCurrency(params.row.lodgingPerDiem),
  },
  
  // Meal Per Diem - Default column
  {
    ...MarginOrderDefaultCell,
    field: 'mealPerDiem',
    headerName: 'Meal Per Diem',
    width: 130,
    valueGetter: (params) => formatCurrency(params.row.mealPerDiem),
  },
  
  // Created - Not a default column
  {
    ...MarginOrderDefaultCell,
    field: 'created',
    headerName: 'Created',
    width: 150,
    hide: true,
    valueGetter: (params) => formatDateTime(params.row.created),
  },
  
  // Created BY - Not a default column
  {
    ...MarginOrderDefaultCell,
    field: 'createdBy',
    headerName: 'Created By',
    width: 120,
    hide: true,
  },
  
  // Last Updated - Not a default column
  {
    ...MarginOrderDefaultCell,
    field: 'lastUpdated',
    headerName: 'Last Updated',
    width: 150,
    hide: true,
    valueGetter: (params) => formatDateTime(params.row.lastUpdated),
  },
  
  // Last Updated By - Not a default column
  {
    ...MarginOrderDefaultCell,
    field: 'lastUpdatedBy',
    headerName: 'Last Updated By',
    width: 150,
    hide: true,
  },
  
  // Order Created - Not a default column
  {
    ...MarginOrderDefaultCell,
    field: 'orderCreated',
    headerName: 'Order Created',
    width: 150,
    hide: true,
    valueGetter: (params) => formatDateTime(params.row.orderCreated),
  },
  
  // Order Verified - Not a default column
  {
    ...MarginOrderDefaultCell,
    field: 'orderVerified',
    headerName: 'Order Verified',
    width: 150,
    hide: true,
    valueGetter: (params) => formatDate(params.row.orderVerified),
  },
  
  // Unit - Default column
  {
    ...MarginOrderDefaultCell,
    field: 'unit',
    headerName: 'Unit',
    width: 100,
  },
  
  // Affiliate - Not a default column
  {
    ...MarginOrderDefaultCell,
    field: 'affiliate',
    headerName: 'Affiliate',
    width: 120,
    hide: true,
  },
  
  // Facility Status - Not a default column
  {
    ...MarginOrderDefaultCell,
    field: 'facilityStatus',
    headerName: 'Facility Status',
    width: 130,
    hide: true,
  },
  
  // Order Created By - Not a default column
  {
    ...MarginOrderDefaultCell,
    field: 'orderCreatedBy',
    headerName: 'Order Created By',
    width: 150,
    hide: true,
  },
  
  // Order Last Updated By - Not a default column
  {
    ...MarginOrderDefaultCell,
    field: 'orderLastUpdatedBy',
    headerName: 'Order Last Updated By',
    width: 180,
    hide: true,
  },
];

export const marginOrdersColumnVisibilityModel = {
  orderId: true,
  payPackageStatus: true,
  jdhPostingStatus: true,
  suppressInJdh: false,
  positions: false,
  division: true,
  facilityId: true,
  facility: true,
  discipline: true,
  specialty: true,
  subspecialty: true,
  shift: true,
  minimumRequiredHours: true,
  hoursPerWeek: false,
  hoursPerShift: false,
  targetMarginDefault: true,
  targetMarginDay: false,
  targetMarginNight: false,
  targetMarginDayNight: false,
  payRangeMinMaxPercentage: false,
  defaultWeeklyPay: true,
  actualMargin: true,
  weeklyGrossPay: true,
  minimumWeeklyGrossPay: true,
  maximumWeeklyGrossPay: true,
  billRate: true,
  vmsFee: true,
  otherFee: true,
  payRate: true,
  lodgingPerDiem: true,
  mealPerDiem: true,
  created: false,
  createdBy: false,
  lastUpdated: false,
  lastUpdatedBy: false,
  orderCreated: false,
  orderVerified: false,
  unit: true,
  affiliate: false,
  facilityStatus: false,
  orderCreatedBy: false,
  orderLastUpdatedBy: false,
};

// Data processing function for Margin Orders
export const marginOrdersFlattenData = items => {  
  return items.map((item, index) => {
    const data = {
      id: `${item.orderId}-${index}`,
      indexCounter: index,
      // Order fields
      orderId: item.orderId || missingField,
      previewDrawerViewerId: item.orderId,
      previewDrawerViewerType: 1,
      facility: item.facility || missingField,
      facilityId: item.facilityId || missingField,
      // Other fields
      payPackageStatus: item.payPackageStatus || missingField,
      jdhPostingStatus: item.jdhPostingStatus || missingField,
      suppressInJdh: item.suppress || false,
      positions: item.positions || 0,
      division: item.division || missingField,
      discipline: item.discipline || missingField,
      specialty: item.specialtyAbbr || missingField,
      subspecialty: item.subSpecialtyAbbr || missingField,
      shift: item.shift || missingField,
      minimumRequiredHours: item.minimumRequiredHours || missingField,
      hoursPerWeek: item.hoursPerWeek || missingField,
      hoursPerShift: item.hoursPerShift || missingField,
      targetMarginDefault: item.targetMarginDefault || 0,
      targetMarginDay: item.targetMarginDayShift || 0,
      targetMarginNight: item.targetMarginNightShift || 0,
      targetMarginDayNight: item.targetMarginDayNightShift || 0,
      payRangeMinMaxPercentage: item.payRangeMinMaxPercentage || 0,
      defaultWeeklyPay: item.defaultWeeklyPay || 0,
      actualMargin: item.currentTargetMargin || 0,
      weeklyGrossPay: item.weeklyGrossPay || 0,
      minimumWeeklyGrossPay: item.minimumWeeklyGrossPay || 0,
      maximumWeeklyGrossPay: item.maximumWeeklyGrossPay || 0,
      billRate: item.billRate || 0,
      vmsFee: item.vms || 0,
      otherFee: item.other || 0,
      payRate: item.payRate || 0,
      lodgingPerDiem: item.lodgingPerDiem || 0,
      mealPerDiem: item.mealPerDiem || 0,
      created: item.createdDateAt || missingField,
      createdBy: `${item.createdUserFirst} ${item.createdUserLast}` || missingField,
      lastUpdated: item.updatedDateAt || missingField,
      lastUpdatedBy: `${item.updatedUserFirst} ${item.updatedUserLast}` || missingField,
      orderCreated: item.orderCreatedDate || missingField,
      orderVerified: item.orderVerifiedDate || missingField,
      unit: item.unitName || missingField,
      affiliate: item.affiliateName || missingField,
      facilityStatus: item.facilityStatus || missingField,
      orderCreatedBy: `${item.orderCreatedUserFirst} ${item.orderCreatedUserLast}` || missingField,
      orderLastUpdatedBy: `${item.orderLastUpdatedByFirst} ${item.orderLastUpdatedByLast}` || missingField,
    };
    return data;
  });
};
